import {makeAutoObservable, runInAction, computed, reaction} from 'mobx';
import {Appointment, BookingListItem, BookingModel, BookingResponse, DateRangeFilter} from "../../model/professional/booking-model";
import companyStore from "../../store/company-store";
import toastStore from "../../store/toast-store";
import dashboardStore from "../../store/dashboard-store";
import {HTTP_STATUS_CODE} from "../../utils/UHttp";
import {updateStatusBookingShareStore} from "../../utils/action-share-store";
import professionalStore from '../../store/professional-store';
import { format, startOfDay, endOfDay } from 'date-fns';

/**
 * ViewModel pour le tableau de bord professionnel
 * 
 * Ce ViewModel gère l'état et la logique métier pour l'interface du tableau de bord professionnel.
 * Il suit le pattern MVVM (Model-View-ViewModel) et utilise MobX pour la gestion d'état réactive.
 * Responsable de la récupération des données de réservation, la gestion des filtres, et des calculs de revenus.
 */

class DashboardViewModel {
    currentTime: Date = new Date();
    activeFilter: string = 'all';
    viewMode: 'card' | 'list' = 'card';
    showNewAppointmentForm: boolean = false;
    showRevenuePanel: boolean = false;
    collaboratorId: string | undefined = undefined
    selectedCollaboratorId: string = '';

    isLoading: boolean = false;
    error: string  = "";

    private timerId: number | null = null;

    protected bookingModel: BookingModel;

    /**
     * Constructeur du ViewModel
     * 
     * Initialise l'état observable, démarre l'horloge pour la mise à jour du temps,
     * récupère l'instance du modèle de réservation et lance le chargement des rendez-vous.
     */
    constructor() {
        makeAutoObservable(this);
        this.bookingModel = BookingModel.getInstance();

        reaction(
            () => this.selectedCollaboratorId,
            (collaboratorId) => {
                if (collaboratorId) {
                    this.initializeAppointments();
                }
            }
        );
    }

    get getAllAppointments() {
        return dashboardStore.appointments;
    }

    /**
     * Définit l'état de chargement
     * 
     * Met à jour l'indicateur de chargement pour afficher ou masquer les éléments UI de chargement
     * 
     * @param isLoading - Booléen indiquant si une opération de chargement est en cours
     */
    setLoading(isLoading: boolean)  {
        this.isLoading = isLoading;
    }

    setCollaboratorId (collaboratorId: string  ) {
        this.collaboratorId = String(collaboratorId);
    }

    setSelectedCollaboratorId(id: string): void {
        this.selectedCollaboratorId = id;
    }

    /**
     * Définit le message d'erreur
     * 
     * Enregistre un message d'erreur à afficher dans l'interface utilisateur
     * 
     * @param error - Le message d'erreur à afficher
     */
    setError(error: string) {
        this.error = error;
    }

    /**
     * Initialise tous les composants du ViewModel
     */
    async initialize(): Promise<void> {
        this.currentTime = new Date();

        await this.initializeAppointments();
        
        this.startClock();
    }
    
    /**
     * Initialise les rendez-vous depuis le serveur
     * 
     * Récupère les réservations depuis l'API et les convertit au format attendu par l'interface utilisateur
     */
    async initializeAppointments(): Promise<void> {
        this.setLoading(true);
        this.setError("");

        try {
            if (!this.selectedCollaboratorId) {
                return;
            }

            const selectedCompanyId = companyStore.getIdCompanySelectedDefault;

            const profileExists = professionalStore.getProfileProfessional !== null;
            const profileHasId = profileExists && professionalStore.getProfileProfessional!.id !== undefined;

            if (!selectedCompanyId || !profileHasId) {
                return;
            }

            this.collaboratorId = professionalStore.getProfileProfessional!.id!;

            const today = new Date();
            const dateFilter: DateRangeFilter = {
                startDate: format(startOfDay(today), 'yyyy-MM-dd'),
                endDate: format(endOfDay(today), 'yyyy-MM-dd'),
                mode: 'day'
            };

            const response: BookingResponse = await this.bookingModel.getProfessionalBookings(
                dateFilter,
                "all",
                selectedCompanyId,
                this.selectedCollaboratorId
            );

            if (response.status === HTTP_STATUS_CODE.OK) {
                const responseAppointments: BookingListItem[] = response.data as BookingListItem[];

                if (responseAppointments && responseAppointments.length > 0) {
                    const appointments = responseAppointments.map(booking =>
                    this.convertServerBookingToAppointment(booking));
                    dashboardStore.setAppointments(appointments);
                } else {
                    dashboardStore.setAppointments([]);
                }
            } else {
                toastStore.error(response.message);
            }
        } catch (error: any) {
            toastStore.error((error?.response?.data?.message));
        } finally {
            this.setLoading(false);
        }
    }


    /**
     * Convertit une réservation du format serveur au format utilisé par l'application
     * 
     * Transforme les données brutes reçues de l'API en un objet Appointment compatible
     * avec les composants UI. Gère la conversion des dates, durées et statuts.
     * 
     * @param serverBooking - Objet de réservation reçu du serveur
     * @returns Un objet Appointment formaté pour l'UI
     * @private
     */
    private convertServerBookingToAppointment(serverBooking: BookingListItem): Appointment {
        const startTime = new Date(serverBooking.startTime);
        const time = startTime.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit'
        });

        const endTime = new Date(serverBooking.endTime);
        const durationMs = endTime.getTime() - startTime.getTime();
        const durationMinutes = Math.floor(durationMs / (1000 * 60));

        let status: 'confirmed' | 'cancelled' | 'completed';
        switch(serverBooking.status) {
            case 'cancelled':
                status = 'cancelled';
                break;
            case 'completed':
                status = 'completed';
                break;
            default:
                status = 'confirmed';
        }

        // Handle multi-services format
        const serviceName = serverBooking.services && serverBooking.services.length > 0
            ? serverBooking.services.map(s => s.serviceName).join(', ')
            : serverBooking.serviceName || 'Service non spécifié';

        return {
            id: serverBooking.id,
            time,
            clientName: serverBooking.clientName,
            service: serviceName,
            duration: `${durationMinutes} min`,
            totalPrice: serverBooking.totalPrice,
            status
        };
    }

    /**
     * Formate une date en chaîne lisible
     * 
     * Convertit un objet Date en chaîne au format français (jour, mois, année)
     * 
     * @param date - L'objet Date à formater
     * @returns Chaîne formatée de la date (ex: "12 juin 2025")
     */
    formatDate(date: Date): string {
        return date.toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    }

    /**
     * Formate une heure en chaîne lisible
     * 
     * Convertit un objet Date en chaîne d'heure au format français (24h)
     * 
     * @param date - L'objet Date dont l'heure doit être formatée
     * @returns Chaîne formatée de l'heure (ex: "14:30")
     */
    formatTime(date: Date): string {
        return date.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Convertit une chaîne d'heure en objet Date
     * 
     * Transforme une heure au format "HH:MM" en un objet Date complet en utilisant la date courante
     * 
     * @param timeStr - Chaîne représentant l'heure (format "HH:MM")
     * @returns Objet Date correspondant à l'heure spécifiée à la date du jour
     */
    getAppointmentTime(timeStr: string): Date {
        const [hours, minutes] = timeStr.split(':').map(Number);
        const date = new Date();
        date.setHours(hours, minutes, 0, 0);
        return date;
    }

    /**
     * Calcule l'heure de fin d'un rendez-vous
     * 
     * Utilise l'heure de début et la durée d'un rendez-vous pour déterminer l'heure de fin
     * 
     * @param appointment - L'objet Appointment dont on veut calculer l'heure de fin
     * @returns Objet Date représentant l'heure de fin du rendez-vous
     */
    getAppointmentEndTime(appointment: Appointment): Date {
        const startTime = this.getAppointmentTime(appointment.time);
        const [duration] = appointment.duration.split(' ')[0].split(':').map(Number);
        const endTime = new Date(startTime);
        endTime.setMinutes(endTime.getMinutes() + duration);
        return endTime;
    }

    /**
     * Obtient le prix d'un rendez-vous
     * 
     * Extrait le prix d'un rendez-vous en priorité depuis les données du serveur,
     * ou utilise une valeur par défaut si non disponible
     * 
     * @param appointment - L'objet Appointment dont on veut obtenir le prix
     * @returns Le prix du rendez-vous en nombre
     * @private
     */
    private getAppointmentPrice(appointment: Appointment): number {
        if (appointment.totalPrice) {
            return parseFloat(String(appointment.totalPrice));
        }
        return 0;
    }

    /**
     * Gère le changement de statut d'un rendez-vous
     * 
     * Met à jour le statut d'un rendez-vous spécifique identifié par son ID
     * et déclenche un rendu UI via MobX
     * 
     * @param id - Identifiant unique du rendez-vous à modifier

     */
     async handleStatusChange(id: string): Promise<void> {
            try {
                if(!id) return
                const response = await this.bookingModel.cancelBooking(id);

                if(response.status === HTTP_STATUS_CODE.OK ){
                    toastStore.success(response.message);
                    updateStatusBookingShareStore(id, "cancelled")
                } else {
                    toastStore.error(response.message);
                }

            } catch (error:any) {
                console.error(error?.response?.data?.message)
                toastStore.error(error?.response?.data?.message);
            }
    }

    /**
     * Calcule le total des recettes encaissées du jour
     * 
     * Somme les prix de tous les rendez-vous confirmés et déjà terminés (heure de fin < heure actuelle)
     * 
     * @returns Le montant total des recettes encaissées en euros
     */
    @computed get completedRevenue(): number {
        const completedAppointments = this.getAllAppointments.filter(appointment => {
            return appointment.status === 'completed' || 
                   (appointment.status === 'confirmed' && 
                    this.getAppointmentEndTime(appointment) <= this.currentTime);
        });

        return completedAppointments.reduce((total, appointment) =>
            total + this.getAppointmentPrice(appointment), 0);
    }

    /**
     * Calcule le total des recettes à venir pour la journée
     * 
     * Somme les prix de tous les rendez-vous confirmés mais pas encore commencés
     * 
     * @returns Le montant total des recettes attendues en euros
     */
    @computed get upcomingRevenue(): number {
        const upcomingAppointments = this.getAllAppointments.filter(
            appointment => appointment.status === 'confirmed' && this.isUpcoming(appointment)
        );

        // Calculer le total des recettes à venir en utilisant le prix du service
        return upcomingAppointments.reduce((total, appointment) =>
            total + this.getAppointmentPrice(appointment), 0);
    }

    /**
     * Calcule le total des recettes du jour (encaissées + à venir)
     * 
     * Combine les recettes des rendez-vous terminés et à venir
     * 
     * @returns Le montant total des recettes de la journée en euros
     */
    @computed get totalDailyRevenue(): number {
        return this.completedRevenue + this.upcomingRevenue;
    }
    
    // Méthodes de compatibilité pour ne pas casser les appels existants
    calculateDailyRevenue(): number {
        return this.completedRevenue;
    }
    
    calculateUpcomingRevenue(): number {
        return this.upcomingRevenue;
    }
    
    calculateTotalDailyRevenue(): number {
        return this.totalDailyRevenue;
    }

    /**
     * Vérifie si un rendez-vous est en cours 
     * 
     * Un rendez-vous est en cours s'il est confirmé et si l'heure actuelle 
     * est entre son heure de début et de fin
     * 
     * @param appointment - Le rendez-vous à vérifier
     * @returns true si le rendez-vous est en cours, false sinon
     */
    isOngoing(appointment: Appointment): boolean {
        if (appointment.status === 'cancelled' || appointment.status === 'completed') {
            return false;
        }
        
        const startTime = this.getAppointmentTime(appointment.time);
        const endTime = this.getAppointmentEndTime(appointment);
        return this.currentTime >= startTime && this.currentTime <= endTime;
    }
    
    /**
     * Vérifie si un rendez-vous est à venir
     * 
     * Un rendez-vous est à venir s'il est confirmé et si l'heure actuelle 
     * est avant son heure de début
     * 
     * @param appointment - Le rendez-vous à vérifier
     * @returns true si le rendez-vous est à venir, false sinon
     */
    isUpcoming(appointment: Appointment): boolean {
        if (appointment.status === 'cancelled' || appointment.status === 'completed') {
            return false;
        }
        
        const startTime = this.getAppointmentTime(appointment.time);
        return this.currentTime < startTime;
    }
    
    /**
     * Vérifie si un rendez-vous est passé
     * 
     * Un rendez-vous est passé s'il a le statut 'cancelled' ou 'completed'
     * 
     * @param appointment - Le rendez-vous à vérifier
     * @returns true si le rendez-vous est passé, false sinon
     */
    isPast(appointment: Appointment): boolean {
        return appointment.status === 'cancelled' || appointment.status === 'completed';
    }

    /**
     * Filter appointments based on status and time
     * 
     * Répartit les rendez-vous en trois catégories:
     * - past: rendez-vous avec statut 'cancelled' ou 'completed'
     * - ongoing: rendez-vous confirmés en cours (selon l'heure)
     * - upcoming: rendez-vous confirmés à venir (selon l'heure)
     * 
     * @returns Objet contenant trois tableaux: ongoing, upcoming et past
     */
    filterAppointments() {
        return {
            ongoing: this.getAllAppointments.filter(appointment => this.isOngoing(appointment)),
            upcoming: this.getAllAppointments.filter(appointment => this.isUpcoming(appointment)),
            past: this.getAllAppointments.filter(appointment => this.isPast(appointment))
        };
    }

    /**
     * Met à jour automatiquement le statut des rendez-vous en fonction de l'heure actuelle
     *
     * Vérifie tous les rendez-vous confirmés et change leur statut à "completed"
     * si l'heure de fin est passée. Appelle l'API pour mettre à jour le statut sur le serveur.
     */
    async updateAppointmentStatuses(): Promise<void> {
        const appointments = this.getAllAppointments;
        
        if (appointments && appointments.length > 0) {
            for (const appointment of appointments) {
                if (appointment.status === 'confirmed') {
                    const endTime = this.getAppointmentEndTime(appointment);
                    if (endTime < this.currentTime) {
                        try {
                            const response: BookingResponse = await this.bookingModel.completedBooking(appointment.id);
                            if(response.status === HTTP_STATUS_CODE.OK){
                                dashboardStore.updateAppointment(appointment.id, 'completed');
                            }
                        } catch (error: any) {
                            console.error(error?.response?.data?.message);
                        }
                    }
                }
            }
        }
    }
    
    /**
     * Démarre l'horloge qui met à jour l'heure actuelle périodiquement
     * et déclenche les vérifications automatiques de statuts
     */
    startClock(): void {
        if (this.timerId) {
            clearInterval(this.timerId);
            this.timerId = null;
        }
        
        runInAction(() => {
            this.currentTime = new Date();
        });
        
        this.updateAppointmentStatuses().then()

        // Définir l'intervalle pour la mise à jour périodique toutes les 10 secondes
        this.timerId = window.setInterval(() => {
            runInAction(() => {
                this.currentTime = new Date();
            });
            
            // Appel de la méthode asynchrone sans bloquer l'intervalle
            this.updateAppointmentStatuses().then()
        }, 10000); // 10 secondes
    }

    /**
     * Arrête l'horloge interne
     * 
     * Annule le timer pour éviter les fuites de mémoire ou les mises à jour inutiles
     * lorsque le composant est démonté
     */
    stopClock(): void {
        if (this.timerId) {
            clearInterval(this.timerId);
            this.timerId = null;
        }
    }

    /**
     * Obtient les rendez-vous filtrés en fonction du filtre actif
     * 
     * Applique le filtre sélectionné (tous, en cours, à venir, passés) aux rendez-vous
     * 
     * @returns Objet contenant trois tableaux filtrés: ongoing, upcoming et past
     */
    getFilteredAppointmentsByActiveFilter() {
        const { ongoing, upcoming, past } = this.filterAppointments();

        switch (this.activeFilter) {
            case 'ongoing':
                return { ongoing, upcoming, past };
            case 'upcoming':
                return { ongoing, upcoming, past };
            case 'past':
                return { ongoing, upcoming, past };
            default:
                return { ongoing, upcoming, past };
        }
    }

    /**
     * Définit le filtre actif
     * 
     * Change le filtre de vue des rendez-vous (tous, en cours, à venir, passés)
     * 
     * @param filter - Nom du filtre à appliquer
     */
    setActiveFilter(filter: string): void {
        this.activeFilter = filter;
    }

    /**
     * Définit le mode d'affichage
     * 
     * Change le mode de visualisation entre carte et liste
     * 
     * @param mode - Mode d'affichage ('card' ou 'list')
     */
    setViewMode(mode: 'card' | 'list'): void {
        this.viewMode = mode;
    }

    /**
     * Affiche ou masque le formulaire de nouveau rendez-vous
     * 
     * Contrôle la visibilité du formulaire d'ajout de rendez-vous
     * 
     * @param show - Booléen pour afficher (true) ou masquer (false) le formulaire
     */
    setShowNewAppointmentForm(show: boolean): void {
        this.showNewAppointmentForm = show;
    }

    /**
     * Affiche ou masque le panneau des revenus
     * 
     * Contrôle la visibilité du panneau des statistiques de revenus
     * 
     * @param show - Booléen pour afficher (true) ou masquer (false) le panneau
     */
    setShowRevenuePanel(show: boolean): void {
        this.showRevenuePanel = show;
    }


    /**
     * Libère les ressources utilisées par le ViewModel
     * 
     * Arrête les timers et nettoie les abonnements pour éviter les fuites de mémoire
     * Cette méthode est appelée par React lors du démontage du composant
     */
    dispose(): void {
        this.stopClock();
    }
}

const dashboardViewModel = new DashboardViewModel()

/**
 * Hook personnalisé pour accéder au ViewModel du dashboard professionnel
 * 
 * Permet aux composants React d'accéder facilement à l'instance singleton du ViewModel
 * 
 * @returns L'instance unique du DashboardViewModel
 */
export const useDashboardViewModel = () => {
    return dashboardViewModel;
};

